import { useState } from "react"
import { useForm, Controller, type Control, useController, useWatch } from "react-hook-form"
import { SafeAreaView } from "react-native-safe-area-context"
import { router, useLocalSearchParams } from "expo-router"
import { View, Pressable } from "react-native"
import { Alert } from "./ui/confirm"

import { BrandPicker } from "./brand-picker"
import { UpcInput } from "./upc-input"
import { KeyboardAwareScrollView } from "@src/components/keyboard-aware-scroll-view"
import { trpc } from "utils/trpc"
import { zodResolver } from "@hookform/resolvers/zod"
import { productFormInput } from "@white-labels/input/src/m5azen"
import { z } from "@white-labels/input"
import { ar } from "i18n/ar"
import { Button, Text, TextInput } from "./core"
import { PriceInput } from "./price-input"
import { ProductType } from "../../../packages/db2/src/enums"
import { sharedStyles } from "@src/utils"
import { analytics } from "@src/utils/analytics"
import { scaleCodeToUpc, upcToScaleCode } from "@white-labels/utils"
import { InventoryCollectionPicker } from "./inventory-collection-picker"

type Props = { id?: number }

export function ProductForm(props: Props) {
  const params = useLocalSearchParams<{ upc: string; type?: "weight" }>()
  const editMode = !!props.id
  const utils = trpc.useUtils()

  const createProduct = trpc.products.create.useMutation()
  const nextInSequence = trpc.products.nextInSequence.useMutation()
  const editProduct = trpc.products.edit.useMutation()
  const addProductToCollections = trpc.inventoryCollections.addProductToCollections.useMutation()
  const product = trpc.products.get.useQuery({ id: props.id as number }, { enabled: editMode, retry: false })
  const inventoryCollections = trpc.inventoryCollections.forProduct.useQuery({ productId: props.id as number }, { enabled: editMode })
  const _type = product.data?.type
  const isWeight = params.type === "weight" || _type === "WEIGHT_PRODUCT"
  const [pack, setPack] = useState<"single" | "pack">(product.data?.base?.id ? "pack" : "single")

  const { control, handleSubmit, formState, setValue } = useForm({
    resolver: zodResolver(productFormInput),
    defaultValues: {
      type: isWeight ? ProductType.WEIGHT_PRODUCT : ((_type as "STANDARD_PRODUCT" | "LOCAL_PRODUCT") ?? "STANDARD_PRODUCT"),
      upc: editMode ? product.data?.upc : params.upc,
      name: product.data?.name,
      description: product.data?.description ?? undefined,
      price: product.data?.price,
      cost: product.data?.cost ?? undefined,
      originalPrice: product.data?.originalPrice ?? undefined,
      value: product.data?.value ?? 1,
      baseUpc: product.data?.base?.upc,
      brandId: product.data?.brandId ?? undefined,
      inventoryCollections: inventoryCollections.data?.map((ic) => ic.id) ?? [],
    },
    reValidateMode: "onChange",
    mode: "all",
  })

  const type = useWatch({ control, name: "type" })
  const { isValid } = formState

  const isLoading = addProductToCollections.isPending || createProduct.isPending || editProduct.isPending || (editMode && product.isLoading)
  const disabled = !isValid || isLoading

  const handleFormSubmit = handleSubmit(async ({ inventoryCollections, ...data }) => {
    if (product.data?.id) {
      editProduct.mutate(
        { ...data, id: product.data?.id },
        {
          onSuccess(product) {
            utils.products.list.invalidate()
            utils.products.get.invalidate({ id: product.id })
            utils.products.getByUpc.fetch({ upc: product.upc })
            utils.inventoryCollections.forProduct.invalidate({ productId: product.id })
            router.canGoBack() ? router.back() : router.replace("/dashboard/products")
          },
          onError(error) {
            console.log(error)
            Alert.alert(ar.somethingWentWrong, error.message)
          },
        },
      )
      addProductToCollections.mutate({ productId: product.data?.id, collectionIds: inventoryCollections ?? [] })
    } else {
      createProduct.mutate(data, {
        onSuccess: (product) => {
          utils.products.list.invalidate()
          // utils.products.get.setData({ id: product.id }, product)
          utils.products.get.fetch({ id: product.id })
          utils.products.getByUpc.fetch({ upc: product.upc })
          utils.inventoryCollections.forProduct.invalidate({ productId: product.id })
          addProductToCollections.mutate({ productId: product.id, collectionIds: inventoryCollections ?? [] })
          analytics.logEvent({ name: "product_created", params: { type: product.type } })
          router.canGoBack() ? router.back() : router.replace("/dashboard/products")
        },
        onError(error) {
          console.log(error)
          Alert.alert(ar.somethingWentWrong, error.message)
        },
      })
    }
  })

  const productType = isWeight ? null : (
    <>
      <Text className="text-lg font-black text-gray-700 mb-2 text-left">{ar.productType}</Text>
      <Controller
        control={control}
        name="type"
        render={({ field }) => (
          <>
            <View className="flex-row gap-1 mb-2 flex-wrap">
              <Pressable
                onPress={() => {
                  field.onChange(ProductType.STANDARD_PRODUCT)
                  setValue("upc", "")
                  setValue("value", 1)
                  setValue("baseUpc", undefined)
                  setPack("single")
                }}
                className={`rounded py-2 px-4 items-center ${field.value === ProductType.STANDARD_PRODUCT ? "bg-blue-500" : "bg-white active:opacity-70 hover:bg-gray-50"}`}
              >
                <Text className={`${field.value === ProductType.STANDARD_PRODUCT ? "text-white" : ""} text-left`}>{ar.STANDARD_PRODUCT}</Text>
              </Pressable>
              <Pressable
                onPress={() => {
                  field.onChange(ProductType.LOCAL_PRODUCT)
                  setValue("upc", "")
                  setValue("value", 1)
                  setValue("baseUpc", undefined)
                  setPack("single")
                }}
                className={`rounded py-2 px-4 items-center ${field.value === ProductType.LOCAL_PRODUCT ? "bg-blue-500" : "bg-white active:opacity-70 hover:bg-gray-50"}`}
              >
                <Text className={`${field.value === ProductType.LOCAL_PRODUCT ? "text-white" : ""} text-left`}>{ar.LOCAL_PRODUCT}</Text>
              </Pressable>
              {_type === "STANDARD_PRODUCT" || _type === "LOCAL_PRODUCT" ? null : (
                <Pressable
                  onPress={() => {
                    field.onChange(ProductType.WEIGHT_PRODUCT)
                    setValue("upc", "")
                    setValue("baseUpc", undefined)
                    setValue("value", 1000)
                  }}
                  className={`rounded py-2 px-4 items-center ${field.value === ProductType.WEIGHT_PRODUCT ? "bg-blue-500" : "bg-white active:opacity-70 hover:bg-gray-50"}`}
                >
                  <Text className={`${field.value === ProductType.WEIGHT_PRODUCT ? "text-white" : ""} text-left`}>{ar.WEIGHT_PRODUCT}</Text>
                </Pressable>
              )}
            </View>
          </>
        )}
      />

      {type === ProductType.LOCAL_PRODUCT || type === ProductType.STANDARD_PRODUCT ? ( //
        <ProductPackBaseForm control={control as Control<z.infer<typeof productFormInput>>} pack={pack} setPack={setPack} />
      ) : null}
    </>
  )

  return (
    <KeyboardAwareScrollView className="flex-1 bg-gray-50" contentContainerStyle={sharedStyles.contentContainer} bottomOffset={40} extraKeyboardSpace={40}>
      {productType}
      <Text className="text-lg font-black text-gray-700 mb-2 text-left">{ar.productName}</Text>
      <Controller
        control={control}
        name="name"
        render={({ field }) => (
          <TextInput placeholder={ar.enterProductName} value={field.value} onChangeText={field.onChange} selectTextOnFocus className="mb-2" textAlign="right" />
        )}
      />
      <Controller
        control={control}
        name="upc"
        render={({ field }) => {
          if (type === ProductType.WEIGHT_PRODUCT) {
            return (
              <>
                <Text className="text-lg font-black text-gray-700 mb-2 text-left">{ar.scaleCode}</Text>
                <TextInput
                  placeholder="1 - 99999"
                  value={upcToScaleCode(field.value) ?? ""}
                  onChangeText={(text) => {
                    if (text.trim() === "") field.onChange("")
                    const num = parseInt(text, 10)
                    if (!Number.isNaN(num)) {
                      field.onChange(scaleCodeToUpc(num.toString()))
                    }
                  }}
                  keyboardType="number-pad"
                  className="h-[40] mb-4"
                  textAlign="left"
                />
              </>
            )
          }

          return (
            <>
              <View className="flex-row justify-between items-center">
                <Text className="text-lg font-black text-gray-700 mb-2 text-left">{ar.barcode}</Text>
                {type === ProductType.LOCAL_PRODUCT && (
                  <Pressable
                    className="px-2 justify-center items-center self-stretch active:opacity-70"
                    onPress={async () => {
                      const upc = await nextInSequence.mutateAsync()
                      setValue("upc", `${upc}`)
                    }}
                  >
                    <Text className="text-sm text-blue-500 text-left">{ar.nextInSequence}</Text>
                  </Pressable>
                )}
              </View>
              <UpcInput value={field.value} onValueChange={field.onChange} className="h-[40] mb-4" skipFetching />
            </>
          )
        }}
      />

      <Text className="text-lg font-black text-gray-700 mb-2 text-left">{ar.description}</Text>
      <Controller
        control={control}
        name="description"
        render={({ field: { onChange, value } }) => <TextInput placeholder={ar.enterProductDescription} value={value} onChangeText={onChange} multiline className="mb-2" />}
      />
      <Text className="text-lg font-black text-gray-700 mb-2 text-left">{ar.brand}</Text>
      <Controller
        control={control}
        name="brandId"
        render={({ field }) => {
          return (
            <BrandPicker
              value={field.value ? [Number.parseInt(field.value.toString(), 10)] : []}
              onSelectionChange={(ids) => field.onChange(ids[0] ?? undefined)}
              className="mb-4"
            />
          )
        }}
      />
      <View className="md:grid md:grid-cols-2 md:gap-4">
        <View className="md:col-span-1">
          <Text className="text-lg font-black text-gray-700 mb-2 text-left">{ar.price}</Text>
          <Controller
            control={control}
            name="price"
            render={({ field: { onChange, value } }) => <PriceInput value={value ? Number(value) : undefined} onValueChange={onChange} className="mb-2" />}
          />
        </View>

        <View className="md:col-span-1">
          <Text className="text-lg font-black text-gray-700 mb-2 text-left">{ar.originalPrice}</Text>
          <Controller
            control={control}
            name="originalPrice"
            render={({ field: { onChange, value } }) => <PriceInput value={value ? Number(value) : undefined} onValueChange={onChange} className="mb-3" />}
          />
        </View>
      </View>

      <Text className="text-lg font-black text-gray-700 mb-2 text-left">{ar.inventoryCollections}</Text>
      <Controller
        control={control}
        name="inventoryCollections"
        render={({ field }) => <InventoryCollectionPicker value={field.value} onChange={field.onChange} className="mb-4" />}
      />

      <Button onPress={handleFormSubmit} disabled={disabled} loading={isLoading}>
        {ar.save}
      </Button>
      <SafeAreaView edges={["bottom"]} />
    </KeyboardAwareScrollView>
  )
}

type ProductPackBaseFormProps = {
  control: Control<z.infer<typeof productFormInput>>
  pack: "single" | "pack"
  setPack: (type: "single" | "pack") => void
}

function ProductPackBaseForm({ control, pack, setPack }: ProductPackBaseFormProps) {
  const baseUpc = useController({ control, name: "baseUpc" })
  const value = useController({ control, name: "value" })

  return (
    <>
      <Text className="text-lg font-black text-gray-700 mb-2 text-left">{ar.singleOrPack}</Text>
      <View className="flex-row gap-2 mb-4">
        <Pressable
          onPress={() => {
            setPack("single")
            baseUpc.field.onChange(undefined)
            value.field.onChange(1)
          }}
          className={`rounded py-2 px-4 items-center ${pack === "single" ? "bg-blue-500" : "bg-white active:opacity-70"}`}
        >
          <Text className={`${pack === "single" ? "text-white" : ""} text-left`}>{ar.singleProduct}</Text>
        </Pressable>
        <Pressable onPress={() => setPack("pack")} className={`rounded py-2 px-4 items-center ${pack === "pack" ? "bg-blue-500" : "bg-white active:opacity-70"}`}>
          <Text className={`${pack === "pack" ? "text-white" : ""} text-left`}>{ar.packOfProduct}</Text>
        </Pressable>
      </View>

      {pack === "pack" && (
        <>
          <Text className="text-lg font-black text-gray-700 mb-2 text-left">{ar.valueUnits}</Text>
          <TextInput
            placeholder="1"
            value={value.field.value?.toString()}
            onChangeText={(text) => value.field.onChange(Number.parseInt(text, 10) || 1)}
            keyboardType="number-pad"
          />
          <Text className="text-lg font-black text-gray-700 mb-2 text-left">{ar.baseProductUpc}</Text>
          <UpcInput value={baseUpc.field.value} onValueChange={(text) => baseUpc.field.onChange(text)} withSearch className="mb-4" />
        </>
      )}
    </>
  )
}
